import { useState, useMemo, useRef, useEffect } from "react";
import { useAuth } from "wasp/client/auth";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "wasp/client/operations";
import { getOrganizations, getOrganizationTypes } from "wasp/client/operations";
import { Filter, Plus, Upload, Columns3, Check } from "lucide-react";
import LoadingSpinner from "./layout/LoadingSpinner";
import { EnhancedActionButton } from "../components/ui/enhanced-page-layout";
import { TableFilterLayout } from "../components/ui/table-filter-layout";
import { BulkUploadDialog } from "./bulk-upload/components/BulkUploadDialog";
import { organizationConfig } from "./bulk-upload/configs";
import { useBulkUploadEnabled } from "./hooks/useBulkUploadEnabled";

// ── Column definitions ────────────────────────────────────────────────────────

type ColumnKey =
  | 'name'
  | 'slug'
  | 'type'
  | 'parent'
  | 'province'
  | 'city'
  | 'suburb'
  | 'neighbourhood'
  | 'subOrgs'
  | 'locations';

interface ColumnDef {
  key: ColumnKey;
  label: string;
  defaultVisible: boolean;
  /** Grid col-span for header/row */
  span: number;
  /** Align text for header (optional) */
  align?: 'center';
}

const ALL_COLUMNS: ColumnDef[] = [
  { key: 'name',          label: 'Name',          defaultVisible: true, span: 2 },
  { key: 'slug',          label: 'Slug / OID',    defaultVisible: true, span: 2 },
  { key: 'type',          label: 'Type',          defaultVisible: true, span: 2 },
  { key: 'parent',        label: 'Parent',        defaultVisible: true, span: 1 },
  { key: 'province',      label: 'Province',      defaultVisible: true, span: 1 },
  { key: 'city',          label: 'City / Town',   defaultVisible: true, span: 1 },
  { key: 'suburb',        label: 'Suburb',        defaultVisible: true, span: 1 },
  { key: 'neighbourhood', label: 'Neighbourhood', defaultVisible: true, span: 1 },
  { key: 'subOrgs',       label: 'Sub-Orgs',      defaultVisible: true, span: 1, align: 'center' },
  { key: 'locations',     label: 'Locations',     defaultVisible: true, span: 1, align: 'center' },
];

const STORAGE_KEY = 'admin:organizations:columns';

function loadColumnVisibility(): Record<ColumnKey, boolean> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<Record<ColumnKey, boolean>>;
      // Merge with defaults so new columns added in future get their default
      const result = {} as Record<ColumnKey, boolean>;
      for (const col of ALL_COLUMNS) {
        result[col.key] = col.key in parsed ? parsed[col.key]! : col.defaultVisible;
      }
      return result;
    }
  } catch (_) { /* corrupt storage — fall through */ }

  return Object.fromEntries(
    ALL_COLUMNS.map(c => [c.key, c.defaultVisible])
  ) as Record<ColumnKey, boolean>;
}

function saveColumnVisibility(vis: Record<ColumnKey, boolean>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(vis));
  } catch (_) { /* storage full / unavailable */ }
}

// ── Column picker dropdown ────────────────────────────────────────────────────

function ColumnPicker({
  visibility,
  onChange,
}: {
  visibility: Record<ColumnKey, boolean>;
  onChange: (key: ColumnKey, value: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
          open
            ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-transparent dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800'
        }`}
        title="Show/hide columns"
      >
        <Columns3 className="w-4 h-4" />
        Columns
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg w-48 py-1">
          <p className="px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-gray-700">
            Show / Hide Columns
          </p>
          {ALL_COLUMNS.map(col => (
            <button
              key={col.key}
              onClick={() => onChange(col.key, !visibility[col.key])}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                visibility[col.key]
                  ? 'bg-blue-600 border-blue-600'
                  : 'border-gray-300 dark:border-gray-600'
              }`}>
                {visibility[col.key] && <Check className="w-2.5 h-2.5 text-white" />}
              </span>
              {col.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Compute total grid span for the visible columns */
function totalSpan(visibility: Record<ColumnKey, boolean>): number {
  return ALL_COLUMNS.filter(c => visibility[c.key]).reduce((s, c) => s + c.span, 0);
}

/** Clamp a span so visible columns always fill the 12-col grid proportionally.
 *  Simple approach: use raw span values; if they don't sum to 12 we just let
 *  the grid stretch naturally via auto cols. */
function colSpanClass(span: number): string {
  const map: Record<number, string> = {
    1: 'col-span-1', 2: 'col-span-2', 3: 'col-span-3', 4: 'col-span-4',
    5: 'col-span-5', 6: 'col-span-6', 7: 'col-span-7', 8: 'col-span-8',
  };
  return map[span] ?? 'col-span-1';
}

// ── Page component ────────────────────────────────────────────────────────────

export default function OrganizationsPage() {
  const { data: user } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return <div>Loading...</div>;
  }

  const { data: organizations = [], isLoading, error, refetch } = useQuery(getOrganizations);
  const { data: organizationTypes = [] } = useQuery(getOrganizationTypes);
  const { isEnabled: isBulkUploadEnabled } = useBulkUploadEnabled();

  const [searchTerm, setSearchTerm] = useState("");
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  // Column visibility — persisted in localStorage
  const [columnVisibility, setColumnVisibility] = useState<Record<ColumnKey, boolean>>(
    loadColumnVisibility
  );

  const handleColumnToggle = (key: ColumnKey, value: boolean) => {
    setColumnVisibility(prev => {
      const next = { ...prev, [key]: value };
      saveColumnVisibility(next);
      return next;
    });
  };

  const visibleColumns = useMemo(
    () => ALL_COLUMNS.filter(c => columnVisibility[c.key]),
    [columnVisibility]
  );

  const filteredData = useMemo(() => {
    return (organizations as any[]).filter((org: any) => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const inName    = org.name.toLowerCase().includes(q);
        const inSlug    = org.slug.toLowerCase().includes(q);
        const inOid     = (org.oid || '').toLowerCase().includes(q);
        const inSpec    = (org.specialization || '').toLowerCase().includes(q);
        const inProvince = (org.province?.name || org.provinceCode || '').toLowerCase().includes(q);
        const inCity    = (org.city?.name || org.cityName || '').toLowerCase().includes(q);
        const inSuburb  = (org.suburbName || '').toLowerCase().includes(q);
        const inNbhd    = (org.neighbourhoodName || '').toLowerCase().includes(q);
        if (!inName && !inSlug && !inOid && !inSpec && !inProvince && !inCity && !inSuburb && !inNbhd) return false;
      }
      if (filterType !== "all" && org.organizationTypeId !== filterType) return false;
      if (filterStatus === "active" && !org.isActive) return false;
      if (filterStatus === "inactive" && org.isActive) return false;
      return true;
    });
  }, [organizations, searchTerm, filterType, filterStatus]);

  const uniqueTypes = useMemo(() => organizationTypes as any[], [organizationTypes]);

  // Build TableFilterLayout column/filter arrays from visible columns
  const tableColumns = visibleColumns.map(c => ({
    title: c.label,
    span: c.span,
    align: c.align,
  }));

  const tableFilters = visibleColumns.map(c => {
    if (c.key === 'name') {
      return {
        span: c.span,
        content: (
          <input
            type="text"
            placeholder="Search name, slug, OID, location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        ),
      };
    }
    if (c.key === 'type') {
      return {
        span: c.span,
        content: (
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Types</option>
            {uniqueTypes.map((type: any) => (
              <option key={type.id} value={type.id}>{type.label}</option>
            ))}
          </select>
        ),
      };
    }
    return { span: c.span, content: <div className="h-10" /> };
  });

  return (
    <>
      <div className="w-full">
        {/* Fixed Header */}
        <div className="fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-30 bg-white dark:bg-sidebar-active transition-colors border-b border-gray-200 dark:border-gray-700">
          <div className="px-4 py-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Organizations</h1>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 mt-0.5 sm:mt-1">
                  Manage organizations in the system ({filteredData.length} total)
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <button
                  onClick={() => setShowFilters(f => !f)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md border transition-colors ${
                    showFilters
                      ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-transparent dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                >
                  <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  Filter
                </button>
                <ColumnPicker visibility={columnVisibility} onChange={handleColumnToggle} />
                {isBulkUploadEnabled && (
                  <EnhancedActionButton
                    onClick={() => setShowBulkUpload(true)}
                    icon={Upload}
                    label="Bulk"
                    variant="secondary"
                    className="px-3 py-1.5 sm:py-2 text-xs sm:text-sm"
                  />
                )}
                <EnhancedActionButton
                  onClick={() => navigate('/admin/organizations/new')}
                  icon={Plus}
                  label="Create"
                  className="px-3 py-1.5 sm:py-2 text-xs sm:text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Spacer */}
        <div className="h-28 sm:h-20" />

        {/* Table Header with Filters */}
        <TableFilterLayout
          showFilters={showFilters}
          columns={tableColumns}
          filters={tableFilters}
        />

        {/* Content */}
        <div className="w-full">
          {isLoading && <LoadingSpinner />}
          {error && (
            <div className="p-4 text-center text-red-600">
              Error loading organizations: {error.message}
            </div>
          )}

          {!isLoading && !error && filteredData.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <p className="text-lg font-medium">No organizations found</p>
              <p className="text-sm mt-2">
                {searchTerm || filterType !== 'all'
                  ? "Try adjusting your filters"
                  : "Click 'Create Organization' to add your first organization"}
              </p>
            </div>
          )}

          {filteredData.length > 0 && (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredData.map((org: any) => (
                <div
                  key={org.id}
                  className="px-4 py-2.5 sm:px-6 hover:bg-gray-50 dark:hover:bg-sidebar-hover transition-colors grid grid-cols-2 sm:grid-cols-12 gap-3 sm:gap-4 items-center"
                >
                  {/* name */}
                  {columnVisibility.name && (
                    <div className={colSpanClass(ALL_COLUMNS.find(c => c.key === 'name')!.span)}>
                      <Link
                        to={`/admin/organizations/${org.id}`}
                        className="flex items-center gap-2 hover:text-blue-600 transition-colors"
                      >
                        <span className="font-medium text-sm text-gray-900 dark:text-white hover:text-blue-600">
                          {org.name}
                        </span>
                      </Link>
                      {org.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate max-w-xs">
                          {org.description}
                        </p>
                      )}
                    </div>
                  )}

                  {/* slug / oid */}
                  {columnVisibility.slug && (
                    <div className={colSpanClass(ALL_COLUMNS.find(c => c.key === 'slug')!.span)}>
                      <span className="font-mono text-xs text-gray-600 dark:text-gray-300">
                        {org.slug}
                      </span>
                      {org.oid && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-mono">
                          OID: {org.oid}
                        </p>
                      )}
                    </div>
                  )}

                  {/* type */}
                  {columnVisibility.type && (
                    <div className={colSpanClass(ALL_COLUMNS.find(c => c.key === 'type')!.span)}>
                      <span className="text-xs px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 whitespace-nowrap">
                        {org.organizationType?.label || 'N/A'}
                      </span>
                      {org.scope && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {org.scope}
                        </p>
                      )}
                    </div>
                  )}

                  {/* parent */}
                  {columnVisibility.parent && (
                    <div className={colSpanClass(ALL_COLUMNS.find(c => c.key === 'parent')!.span)}>
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {org.parentOrganization?.name || '-'}
                      </span>
                      {org.specialization && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {org.specialization}
                        </p>
                      )}
                    </div>
                  )}

                  {/* province */}
                  {columnVisibility.province && (
                    <div className={colSpanClass(ALL_COLUMNS.find(c => c.key === 'province')!.span)}>
                      {org.province?.name || org.provinceCode ? (
                        <span className="text-xs text-gray-700 dark:text-gray-300">
                          {org.province?.name ?? org.provinceCode}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </div>
                  )}

                  {/* city */}
                  {columnVisibility.city && (
                    <div className={colSpanClass(ALL_COLUMNS.find(c => c.key === 'city')!.span)}>
                      {org.city?.name || org.cityName ? (
                        <span className="text-xs text-gray-700 dark:text-gray-300">
                          {org.city?.name ?? org.cityName}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </div>
                  )}

                  {/* suburb */}
                  {columnVisibility.suburb && (
                    <div className={colSpanClass(ALL_COLUMNS.find(c => c.key === 'suburb')!.span)}>
                      {org.suburbName ? (
                        <span className="text-xs text-gray-700 dark:text-gray-300">
                          {org.suburbName}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </div>
                  )}

                  {/* neighbourhood */}
                  {columnVisibility.neighbourhood && (
                    <div className={colSpanClass(ALL_COLUMNS.find(c => c.key === 'neighbourhood')!.span)}>
                      {org.neighbourhoodName ? (
                        <span className="text-xs text-gray-700 dark:text-gray-300">
                          {org.neighbourhoodName}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </div>
                  )}

                  {/* sub-orgs */}
                  {columnVisibility.subOrgs && (
                    <div className={`${colSpanClass(ALL_COLUMNS.find(c => c.key === 'subOrgs')!.span)} text-center`}>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {org._count?.subOrganizations || 0}
                      </span>
                    </div>
                  )}

                  {/* locations */}
                  {columnVisibility.locations && (
                    <div className={`${colSpanClass(ALL_COLUMNS.find(c => c.key === 'locations')!.span)} text-center`}>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {org._count?.locationOrganizations || 0}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <BulkUploadDialog
        open={showBulkUpload}
        onOpenChange={setShowBulkUpload}
        config={organizationConfig}
        onComplete={() => refetch()}
      />
    </>
  );
}



